import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { 
  getCompanyStatuses, 
  saveCompanyStatuses, 
  addCompanyStatus, 
  updateCompanyStatus, 
  deleteCompanyStatus 
} from '../utils/statuses';

export default function StatusEditor({ isDarkMode, onClose, isModal = false, currentUserId = null, userRole = null }) {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper funkce pro získání ID stavu
  const getStatusId = (status) => status._id || status.id;

  // Načti stavy při načtení komponenty
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        setLoading(true);
        setError(null);
        const loadedStatuses = await getCompanyStatuses();
        setStatuses(loadedStatuses);
      } catch (err) {
        setError('Chyba při načítání stavů: ' + err.message);
        console.error('Chyba při načítání stavů:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadStatuses();
  }, []);

  // BEZPEČNOSTNÍ KONTROLA - pouze pro adminy (musí být AŽ PO hooks)
  if (userRole !== 'admin' && userRole !== 'teamleader' && userRole !== 'superadmin') {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">🚫 Přístup odepřen</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Tato stránka je dostupná pouze administrátorům.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Zpět na hlavní stránku
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Přidání nového stavu
  const handleAddStatus = async () => {
    if (newStatus.trim() && !statuses.some(s => s.name === newStatus.trim())) {
      try {
        const newStatusObj = await addCompanyStatus(newStatus.trim(), currentUserId);
        setStatuses(prev => [...prev, newStatusObj]);
        setNewStatus('');
        setError(null);
      } catch (err) {
        setError('Chyba při přidávání stavu: ' + err.message);
        console.error('Chyba při přidávání stavu:', err);
      }
    }
  };

  // Úprava stavu
  const handleEditStatus = async (statusId, newName) => {
    if (newName.trim() && !statuses.some(s => s.name === newName.trim() && getStatusId(s) !== statusId)) {
      try {
        const updatedStatus = await updateCompanyStatus(statusId, newName.trim());
        setStatuses(prev => prev.map(status =>
          getStatusId(status) === statusId ? updatedStatus : status
        ));
        setEditingId(null);
        setEditingText('');
        setError(null);
      } catch (err) {
        setError('Chyba při aktualizaci stavu: ' + err.message);
        console.error('Chyba při aktualizaci stavu:', err);
      }
    }
  };

  // Smazání stavu
  const handleDeleteStatus = async (statusId) => {
    try {
      await deleteCompanyStatus(statusId);
      setStatuses(prev => prev.filter(status => getStatusId(status) !== statusId));
      setError(null);
    } catch (err) {
      setError('Chyba při mazání stavu: ' + err.message);
      console.error('Chyba při mazání stavu:', err);
    }
  };

  // Přepnutí excludeFromOverdue stavu
  const handleToggleExcludeFromOverdue = async (statusId, excludeValue) => {
    try {
      const updatedStatus = await updateCompanyStatus(statusId, null, { excludeFromOverdue: excludeValue });
      setStatuses(prev => prev.map(status =>
        getStatusId(status) === statusId ? updatedStatus : status
      ));
      setError(null);
    } catch (err) {
      setError('Chyba při aktualizaci stavu: ' + err.message);
      console.error('Chyba při aktualizaci excludeFromOverdue:', err);
    }
  };

  // Přepnutí autoSetForecastToZero stavu
  const handleToggleAutoSetForecastToZero = async (statusId, value) => {
    try {
      const updatedStatus = await updateCompanyStatus(statusId, null, { autoSetForecastToZero: value });
      setStatuses(prev => prev.map(status =>
        getStatusId(status) === statusId ? updatedStatus : status
      ));
      setError(null);
    } catch (err) {
      setError('Chyba při aktualizaci stavu: ' + err.message);
      console.error('Chyba při aktualizaci autoSetForecastToZero:', err);
    }
  };

  // Přepnutí requireForecast stavu
  const handleToggleRequireForecast = async (statusId, value) => {
    try {
      const updatedStatus = await updateCompanyStatus(statusId, null, { requireForecast: value });
      setStatuses(prev => prev.map(status =>
        getStatusId(status) === statusId ? updatedStatus : status
      ));
      setError(null);
    } catch (err) {
      setError('Chyba při aktualizaci stavu: ' + err.message);
      console.error('Chyba při aktualizaci requireForecast:', err);
    }
  };

  // Změna požadované hodnoty Forecast
  const handleChangeRequiredForecastValue = async (statusId, value) => {
    try {
      const updatedStatus = await updateCompanyStatus(statusId, null, { requiredForecastValue: value });
      setStatuses(prev => prev.map(status =>
        getStatusId(status) === statusId ? updatedStatus : status
      ));
      setError(null);
    } catch (err) {
      setError('Chyba při aktualizaci stavu: ' + err.message);
      console.error('Chyba při aktualizaci requiredForecastValue:', err);
    }
  };

  // Drag & Drop funkce
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    try {
      console.log('Starting drag & drop operation...');
      console.log('Current statuses:', statuses);
      console.log('Dragged from:', draggedIndex, 'to:', dropIndex);
      
      const newStatuses = [...statuses];
      const draggedItem = newStatuses[draggedIndex];
      
      console.log('Dragged item:', draggedItem);
      
      // Odstranění původního prvku
      newStatuses.splice(draggedIndex, 1);
      // Vložení na novou pozici
      newStatuses.splice(dropIndex, 0, draggedItem);
      
      // Aktualizace pořadí
      const updatedStatuses = newStatuses.map((status, index) => ({
        ...status,
        order: index
      }));
      
      console.log('Updated statuses with new order:', updatedStatuses);
      
      // Optimisticky aktualizuj UI
      setStatuses(updatedStatuses);
      setDraggedIndex(null);
      
      // Ulož na backend
      console.log('Saving to backend...');
      const result = await saveCompanyStatuses(updatedStatuses);
      console.log('Backend save result:', result);
      
      // Aktualizuj UI s výsledkem z backendu (pokud existuje)
      if (result && Array.isArray(result)) {
        setStatuses(result);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error during drag & drop:', err);
      setError('Chyba při přeřazování stavů: ' + err.message);
      
      // Znovu načti stavy z backendu v případě chyby
      try {
        console.log('Reloading statuses from backend...');
        const freshStatuses = await getCompanyStatuses();
        setStatuses(freshStatuses);
      } catch (reloadErr) {
        console.error('Chyba při znovu načítání stavů:', reloadErr);
        setError('Chyba při přeřazování stavů: ' + err.message + '. Nepodařilo se obnovit stavy: ' + reloadErr.message);
      }
    }
  };

  return (
    <div className={`${isDarkMode && !isModal ? 'dark' : ''}`}>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Načítám stavy...
            </div>
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="text-red-700 dark:text-red-300 text-sm">
                ⚠️ {error}
              </div>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                Zavřít
              </button>
            </div>
          )}
          
          {isModal ? (
        // Obsah pro modální okno - bez pozadí a headeru
        <div className="space-y-6">
          {/* Přidání nového stavu */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              ➕ Přidat nový stav
            </h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                placeholder="Název nového stavu..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                onKeyPress={(e) => e.key === 'Enter' && handleAddStatus()}
              />
              <button
                onClick={handleAddStatus}
                disabled={!newStatus.trim() || statuses.some(s => s.name === newStatus.trim())}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold transition"
              >
                Přidat
              </button>
            </div>
            {newStatus.trim() && statuses.some(s => s.name === newStatus.trim()) && (
              <p className="text-red-500 text-sm mt-2">
                ⚠️ Stav s tímto názvem už existuje
              </p>
            )}
          </div>

          {/* Seznam stavů */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              📋 Existující stavy ({statuses.length})
            </h3>
            
            {statuses.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <div className="text-4xl mb-2">📝</div>
                <div>Žádné stavy nejsou definovány</div>
                <div className="text-sm mt-1">Přidejte první stav pomocí formuláře výše</div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  💡 Přetáhněte stavy pro změnu pořadí
                </div>
                
                    {statuses.map((status, index) => (
                      <div
                        key={getStatusId(status)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`
                          p-3 border border-gray-200 dark:border-gray-600 rounded-lg
                          ${draggedIndex === index ? 'opacity-50' : 'opacity-100'}
                          hover:bg-gray-50 dark:hover:bg-gray-700 cursor-move transition-all
                        `}
                      >
                        <div className="flex items-start gap-3">
                          {/* Drag handle */}
                          <div className="text-gray-400 cursor-move pt-1">
                            ⋮⋮
                          </div>
                          
                          {/* Pořadí */}
                          <div className="w-8 text-center text-sm font-medium text-gray-500 dark:text-gray-400 pt-1">
                            {index + 1}.
                          </div>
                          
                          {/* Obsah */}
                          <div className="flex-1">
                            {/* Název stavu */}
                            <div className="mb-3">
                              {editingId === getStatusId(status) ? (
                                <input
                                  type="text"
                                  value={editingText}
                                  onChange={(e) => setEditingText(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleEditStatus(getStatusId(status), editingText);
                                    if (e.key === 'Escape') {
                                      setEditingId(null);
                                      setEditingText('');
                                    }
                                  }}
                                  onBlur={() => handleEditStatus(getStatusId(status), editingText)}
                                  className="w-full px-2 py-1 border border-blue-300 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  autoFocus
                                />
                              ) : (
                                <span className="text-gray-900 dark:text-white font-medium text-base">
                                  {status.name}
                                </span>
                              )}
                            </div>
                            
                            {/* Grid s checkboxy */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              {/* Checkbox pro vyloučení z propadlých aktivit */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`exclude-overdue-${getStatusId(status)}`}
                                  checked={status.excludeFromOverdue || false}
                                  onChange={(e) => handleToggleExcludeFromOverdue(getStatusId(status), e.target.checked)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label
                                  htmlFor={`exclude-overdue-${getStatusId(status)}`}
                                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  title="Pokud je zaškrtnuto, kontakty v tomto stavu se nezapočítají do propadlých aktivit"
                                >
                                  Nezapočítávat do propadlých
                                </label>
                              </div>
                              
                              {/* Checkbox pro automatické nastavení Forecast na 0 */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`auto-forecast-zero-${getStatusId(status)}`}
                                  checked={status.autoSetForecastToZero || false}
                                  onChange={(e) => handleToggleAutoSetForecastToZero(getStatusId(status), e.target.checked)}
                                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                                />
                                <label
                                  htmlFor={`auto-forecast-zero-${getStatusId(status)}`}
                                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  title="Automaticky nastavit Forecast na 0 při volbě tohoto stavu"
                                >
                                  Automaticky dát Forecast do 0
                                </label>
                              </div>
                              
                              {/* Checkbox pro povinné vyplnění Forecast */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`require-forecast-${getStatusId(status)}`}
                                  checked={status.requireForecast || false}
                                  onChange={(e) => handleToggleRequireForecast(getStatusId(status), e.target.checked)}
                                  className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                />
                                <label
                                  htmlFor={`require-forecast-${getStatusId(status)}`}
                                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  title="Vyžadovat vyplnění Forecast při volbě tohoto stavu"
                                >
                                  Nutno vyplnit Forecast
                                </label>
                              </div>
                              
                              {/* Select pro požadovanou hodnotu Forecast */}
                              {status.requireForecast && (
                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    Hodnota:
                                  </label>
                                  <select
                                    value={status.requiredForecastValue || ''}
                                    onChange={(e) => handleChangeRequiredForecastValue(getStatusId(status), e.target.value)}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  >
                                    <option value="">Libovolná</option>
                                    <option value="0">0%</option>
                                    <option value="25">25%</option>
                                    <option value="50">50%</option>
                                    <option value="75">75%</option>
                                    <option value="99">99%</option>
                                    <option value="100">100%</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Akce */}
                          <div className="flex gap-2 pt-1">
                            {editingId === getStatusId(status) ? (
                              <>
                                <button
                                  onClick={() => handleEditStatus(getStatusId(status), editingText)}
                                  className="text-green-600 hover:text-green-800 p-1"
                                  title="Uložit"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingId(null);
                                    setEditingText('');
                                  }}
                                  className="text-gray-600 hover:text-gray-800 p-1"
                                  title="Zrušit"
                                >
                                  ✗
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingId(getStatusId(status));
                                    setEditingText(status.name);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Upravit"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Opravdu chcete smazat stav "${status.name}"?`)) {
                                      handleDeleteStatus(getStatusId(status));
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Smazat"
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
              </div>
            )}
          </div>
          
          {/* Nápověda */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
              💡 Nápověda
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Stavy můžete přetahovat pro změnu pořadí</li>
              <li>• Klikněte na tužku pro úpravu názvu stavu</li>
              <li>• Nové stavy se automaticky přidají na konec seznamu</li>
              <li>• Změny se ukládají automaticky do localStorage</li>
            </ul>
          </div>
        </div>
      ) : (
        // Plnohodnotná stránka pro samostatné otevření
        <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-900 dark:to-gray-800 py-6">
          <div className="max-w-6xl mx-auto px-4">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
              <div className="p-6">
                <PageHeader
                  title="⚙️ Editor stavů firem"
                  showBackButton={true}
                  backText="Zpět"
                  onBack={onClose}
                />
              </div>
            </div>

            {/* Přidání nového stavu */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  ➕ Přidat nový stav
                </h2>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    placeholder="Název nového stavu..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddStatus()}
                  />
                  <button
                    onClick={handleAddStatus}
                    disabled={!newStatus.trim() || statuses.some(s => s.name === newStatus.trim())}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold transition"
                  >
                    Přidat
                  </button>
                </div>
                {newStatus.trim() && statuses.some(s => s.name === newStatus.trim()) && (
                  <p className="text-red-500 text-sm mt-2">
                    ⚠️ Stav s tímto názvem už existuje
                  </p>
                )}
              </div>
            </div>

            {/* Seznam stavů */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  📋 Existující stavy ({statuses.length})
                </h2>
                
                {statuses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <div className="text-4xl mb-2">📝</div>
                    <div>Žádné stavy nejsou definovány</div>
                    <div className="text-sm mt-1">Přidejte první stav pomocí formuláře výše</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      💡 Přetáhněte stavy pro změnu pořadí
                    </div>
                    
                    {statuses.map((status, index) => (
                      <div
                        key={getStatusId(status)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`
                          p-3 border border-gray-200 dark:border-gray-600 rounded-lg
                          ${draggedIndex === index ? 'opacity-50' : 'opacity-100'}
                          hover:bg-gray-50 dark:hover:bg-gray-700 cursor-move transition-all
                        `}
                      >
                        <div className="flex items-start gap-3">
                          {/* Drag handle */}
                          <div className="text-gray-400 cursor-move pt-1">
                            ⋮⋮
                          </div>
                          
                          {/* Pořadí */}
                          <div className="w-8 text-center text-sm font-medium text-gray-500 dark:text-gray-400 pt-1">
                            {index + 1}.
                          </div>
                          
                          {/* Obsah */}
                          <div className="flex-1">
                            {/* Název stavu */}
                            <div className="mb-3">
                              {editingId === getStatusId(status) ? (
                                <input
                                  type="text"
                                  value={editingText}
                                  onChange={(e) => setEditingText(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleEditStatus(getStatusId(status), editingText);
                                    if (e.key === 'Escape') {
                                      setEditingId(null);
                                      setEditingText('');
                                    }
                                  }}
                                  onBlur={() => handleEditStatus(getStatusId(status), editingText)}
                                  className="w-full px-2 py-1 border border-blue-300 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  autoFocus
                                />
                              ) : (
                                <span className="text-gray-900 dark:text-white font-medium text-base">
                                  {status.name}
                                </span>
                              )}
                            </div>
                            
                            {/* Grid s checkboxy */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              {/* Checkbox pro vyloučení z propadlých aktivit */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`exclude-overdue-full-${getStatusId(status)}`}
                                  checked={status.excludeFromOverdue || false}
                                  onChange={(e) => handleToggleExcludeFromOverdue(getStatusId(status), e.target.checked)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label
                                  htmlFor={`exclude-overdue-full-${getStatusId(status)}`}
                                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  title="Pokud je zaškrtnuto, kontakty v tomto stavu se nezapočítají do propadlých aktivit"
                                >
                                  Nezapočítávat do propadlých
                                </label>
                              </div>
                              
                              {/* Checkbox pro automatické nastavení Forecast na 0 */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`auto-forecast-zero-full-${getStatusId(status)}`}
                                  checked={status.autoSetForecastToZero || false}
                                  onChange={(e) => handleToggleAutoSetForecastToZero(getStatusId(status), e.target.checked)}
                                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                                />
                                <label
                                  htmlFor={`auto-forecast-zero-full-${getStatusId(status)}`}
                                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  title="Automaticky nastavit Forecast na 0 při volbě tohoto stavu"
                                >
                                  Automaticky dát Forecast do 0
                                </label>
                              </div>
                              
                              {/* Checkbox pro povinné vyplnění Forecast */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`require-forecast-full-${getStatusId(status)}`}
                                  checked={status.requireForecast || false}
                                  onChange={(e) => handleToggleRequireForecast(getStatusId(status), e.target.checked)}
                                  className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                />
                                <label
                                  htmlFor={`require-forecast-full-${getStatusId(status)}`}
                                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  title="Vyžadovat vyplnění Forecast při volbě tohoto stavu"
                                >
                                  Nutno vyplnit Forecast
                                </label>
                              </div>
                              
                              {/* Select pro požadovanou hodnotu Forecast */}
                              {status.requireForecast && (
                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    Hodnota:
                                  </label>
                                  <select
                                    value={status.requiredForecastValue || ''}
                                    onChange={(e) => handleChangeRequiredForecastValue(getStatusId(status), e.target.value)}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  >
                                    <option value="">Libovolná</option>
                                    <option value="0">0%</option>
                                    <option value="25">25%</option>
                                    <option value="50">50%</option>
                                    <option value="75">75%</option>
                                    <option value="99">99%</option>
                                    <option value="100">100%</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Akce */}
                          <div className="flex gap-2 pt-1">
                            {editingId === getStatusId(status) ? (
                              <>
                                <button
                                  onClick={() => handleEditStatus(getStatusId(status), editingText)}
                                  className="text-green-600 hover:text-green-800 p-1"
                                  title="Uložit"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingId(null);
                                    setEditingText('');
                                  }}
                                  className="text-gray-600 hover:text-gray-800 p-1"
                                  title="Zrušit"
                                >
                                  ✗
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingId(getStatusId(status));
                                    setEditingText(status.name);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Upravit"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Opravdu chcete smazat stav "${status.name}"?`)) {
                                      handleDeleteStatus(getStatusId(status));
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Smazat"
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Nápověda */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                💡 Nápověda
              </h3>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <li>• Stavy můžete přetahovat pro změnu pořadí</li>
                <li>• Klikněte na tužku pro úpravu názvu stavu</li>
                <li>• Nové stavy se automaticky přidají na konec seznamu</li>
                <li>• Změny se ukládají automaticky do backendu</li>
              </ul>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
